import { createUseStyles } from 'react-jss';
import { CmsWrenchTheme } from '~app/styles/cmsTheme';
import { ColorProp } from '.';

type StyleProps = {
  color: ColorProp;
  showLogoWall: boolean;
};

const variantClassMapping = (theme: CmsWrenchTheme): Record<string, object> => ({
  driveGetResults2025Variant2Container: {
    [`@media (min-width: ${theme.breakpoints.md}px)`]: {
      paddingTop: '80px',
    },
  },
  driveGetResults2025Variant1BoxSection: {
    paddingTop: '0px',
  },
  driveGetResults2025Variant2BoxSection: {
    paddingTop: '0px',
    display: 'flex',
    flexDirection: 'column',
    rowGap: '24px',
  },
  driveGetResults2025Variant2CopyText: {
    fontSize: theme.type.fontSize.sectionTitleSm,
    lineHeight: '40px',
  },
  driveGetResults2025Variant2Button: {
    '& > *': {
      backgroundColor: theme.brandColors.primary.vine,
      borderColor: theme.brandColors.neutral.white,
      color: theme.brandColors.neutral.white,
    },
    '&:hover, &:focus': {
      backgroundColor: 'rgb(77, 88, 111)',
      borderColor: theme.endpageColors.onHover.transparentWhite,
      textDecoration: 'none',
      boxShadow: 'rgba(0, 0, 0, 0.14) 0px 2px 4px 0px',
    },
    '&:focus-visible': {
      // default focus outline not visible for some reason, so we have to apply manually
      outline: '2px solid #007aff',
    },
  },
  driveGetResults2025Variant2G2RatingsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: 12,
    '&>p': {
      fontWeight: 500,
      fontSize: theme.type.fontSize.body,
      letterSpacing: '0%',
      textAlign: 'center',
      [`@media (min-width: ${theme.breakpoints.sm}px)`]: {
        fontSize: theme.type.fontSize.cardTitle,
      },
    },
    '&>img': {
      width: '100%',
      maxWidth: 210,
      marginBottom: 0,
      [`@media (min-width: ${theme.breakpoints.md}px)`]: {
        paddingLeft: theme.spacing[4],
      },
      [`@media (min-width: ${theme.breakpoints.lg}px)`]: {
        paddingLeft: 0,
      },
    },
  },
  driveGetResults2025Variant3BoxSection: {
    [`@media (min-width: ${theme.breakpoints.md}px)`]: {
      paddingTop: '100px',
    },
  },
});

const useStyles = createUseStyles((theme: CmsWrenchTheme) => ({
  container: ({ color }: StyleProps) => {
    const backgroundColor = (): string | undefined => {
      const COLOR_STRING_TO_VALUE = {
        sabaeus: theme.brandColors.primary.sabaeus,
        canvas: theme.brandColors.neutral.canvas,
        vine: theme.brandColors.primary.vine,
        white: theme.brandColors.neutral.white,
        greyOil: theme.brandColors.primary.greyOil,
      };
      return COLOR_STRING_TO_VALUE[color];
    };
    return {
      backgroundColor: backgroundColor(),
    };
  },
  button: ({ color }: StyleProps) => ({
    '& > *': {
      backgroundColor: color === 'sabaeus' ? theme.brandColors.neutral.white : undefined,
      borderColor: color === 'sabaeus' ? theme.brandColors.neutral.white : undefined,
      color: color === 'sabaeus' ? theme.brandColors.primary.sabaeus : undefined,
      '&:hover, &:focus': {
        backgroundColor: color === 'sabaeus' ? theme.endpageColors.onHover.transparentWhite : undefined,
        borderColor: color === 'sabaeus' ? theme.endpageColors.onHover.transparentWhite : undefined,
        textDecoration: 'none',
      },
      '&:focus-visible': {
        // default focus outline not visible for some reason, so we have to apply manually
        outline: '2px solid #007aff',
      },
    },
  }),
  copyElement: {
    maxWidth: 600,
  },
  copyText: ({ color, showLogoWall }: StyleProps) => {
    let textColor: string | undefined;
    if (color === 'canvas' && !showLogoWall) {
      textColor = theme.brandColors.primary.sabaeus;
    } else if (color === 'sabaeus' || color === 'vine' || color === 'greyOil') {
      textColor = theme.brandColors.neutral.white;
    } else if (color === 'white') {
      textColor = theme.brandColors.neutral.black;
    }
    return {
      color: textColor,
      fontWeight: 500,
    };
  },
  imagePlacement: {
    width: '100%',
    maxWidth: 604,
    marginBottom: 0,
    [`@media (min-width: ${theme.breakpoints.md}px)`]: {
      paddingLeft: theme.spacing[4],
    },
    [`@media (min-width: ${theme.breakpoints.lg}px)`]: {
      paddingLeft: 0,
    },
  },

  ...variantClassMapping(theme),
}));

export default useStyles;
